import React, { useEffect, useState } from 'react';
import { FiZap, FiSave, FiArrowUp, FiArrowDown, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import services from '../../services';
import AppModal from '../../common/AppModal';
import AppSelect from '../../common/AppSelect';
import { PROPERTY_STATUS_LINK_TYPE } from './PropertyStatusLinkForm';

export const LIST_PROCESS_TYPE = 'listProcess';

const WHEN_OPTIONS = [
    { value: 'new', label: 'a new mail is received' },
    { value: 'itemAdded', label: 'an item is added / moved to a list' },
    { value: 'noReplyToCustomer', label: 'there is no reply to a customer' },
    { value: 'noReplyFromCustomer', label: 'there is no reply from a customer' },
    { value: PROPERTY_STATUS_LINK_TYPE, label: 'a property status is set on a ticket' },
    { value: LIST_PROCESS_TYPE, label: 'tickets must follow the lists in order (ticket process)' },
    { value: 'ticketPriority', label: 'tickets are ordered by status priority (ticket priority)' },
];

const REQUIREMENT_OPTIONS = [
    { value: 'mandatory', label: 'Mandatory' },
    { value: 'optional', label: 'Optional' },
];

const selectStyle = { height: '38px', fontSize: '13px', minWidth: '200px' };
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#1a7d3e', width: '60px' };
const iconBtn = {
    width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', color: '#374151',
};

const emptyStep = () => ({ listId: '', mandatory: true });

/**
 * Create / edit the "ticket process" of a board: the order of lists every ticket must follow.
 * Stored as a Rule of type 'listProcess' (one per board).
 */
const ListProcessForm = ({ show, onHide, onCancel, editId, boardName, onTypeChange, onSaved }) => {
    const [boardId, setBoardId] = useState('');
    const [steps, setSteps] = useState([emptyStep(), emptyStep()]);
    const [listOptions, setListOptions] = useState([]);
    const [existingRule, setExistingRule] = useState(null);
    const [saving, setSaving] = useState(false);

    // Lists of the board, in board order
    useEffect(() => {
        if (!boardId) {
            setListOptions([]);
            return;
        }
        (async () => {
            try {
                const { data } = await services.board.SELECTED_BOARD(boardId);
                setListOptions(
                    (data || [])
                        .slice()
                        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
                        .map((item) => ({ value: item.id, label: item.coloum })),
                );
            } catch (e) {
                console.error(e);
                setListOptions([]);
            }
        })();
    }, [boardId]);

    // One process per board: warn when the board already has one
    useEffect(() => {
        setExistingRule(null);
        if (!boardId) return;
        (async () => {
            try {
                const rules = await services.automations.GET_ALL_AUTOMATIONS(`type=${LIST_PROCESS_TYPE}&boardId=${boardId}`);
                setExistingRule((rules || []).find((r) => r.id !== editId) || null);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [boardId, editId]);

    // Edit mode: load the saved rule
    useEffect(() => {
        if (!show) return;
        if (!editId) {
            setBoardId('');
            setSteps([emptyStep(), emptyStep()]);
            return;
        }
        (async () => {
            try {
                const response = await services.automations.GET_BY_ID_AUTOMATIONS(editId);
                const rule = response?.data?.data;
                if (!rule) return;
                setBoardId(rule.when?.boardId || (rule.condition || []).find((c) => c.operand === 'board')?.value || '');
                const saved = (rule.action || [])
                    .filter((a) => a.action === 'processStep')
                    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
                    .map((a) => ({ listId: a.listId || '', mandatory: a.mandatory !== false }));
                setSteps(saved.length ? saved : [emptyStep(), emptyStep()]);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [show, editId]);

    const updateStep = (index, patch) => setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    const moveStep = (index, delta) =>
        setSteps((prev) => {
            const to = index + delta;
            if (to < 0 || to >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[to]] = [next[to], next[index]];
            return next;
        });

    const label = (value) => listOptions.find((o) => o.value === value)?.label || '';

    const handleSave = async () => {
        if (!boardId) return toast.error('Please select the board');
        if (existingRule) return toast.error('This board already has a ticket process: edit that one instead');
        const filled = steps.filter((s) => s.listId);
        if (filled.length < 2) return toast.error('Please select at least 2 lists');
        if (filled.length !== steps.length) return toast.error('Please select a list for every step, or remove the empty step');
        if (!filled.some((s) => s.mandatory)) return toast.error('At least one step must be mandatory');

        const payload = {
            type: LIST_PROCESS_TYPE,
            name: `Ticket process: ${filled.map((s) => label(s.listId)).join(' -> ')}`,
            when: { boardId },
            checklist: [],
            condition: [{ operand: 'board', operator: 'equals', value: boardId }],
            action: filled.map((s, i) => ({ action: 'processStep', order: i, boardId, listId: s.listId, mandatory: s.mandatory })),
        };

        setSaving(true);
        try {
            const response = !editId
                ? await services.automations.CREATE_AUTOMATIONS(payload)
                : await services.automations.UPDATE_AUTOMATIONS(editId, payload);
            if (response) {
                toast.success(editId ? 'Automations Updated Successfully' : 'Automations Added Successfully');
                onSaved && onSaved();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div style={{ display: 'flex', gap: '10px' }}>
            <button
                type="button"
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#374151',
                    backgroundColor: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                }}
                onClick={onCancel || onHide}
            >
                Cancel
            </button>
            <button
                type="button"
                disabled={saving}
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#fff',
                    backgroundColor: '#1a7d3e', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: saving ? 0.7 : 1,
                }}
                onClick={handleSave}
            >
                <FiSave size={13} />
                {editId ? 'Update' : 'Create'}
            </button>
        </div>
    );

    const firstMandatory = steps.findIndex((s) => s.mandatory && s.listId);
    const entryNames = steps
        .slice(0, firstMandatory < 0 ? 0 : firstMandatory + 1)
        .map((s) => label(s.listId))
        .filter(Boolean);

    return (
        <AppModal
            show={show}
            onClose={onHide}
            icon={<FiZap size={17} color="#fff" />}
            title={editId ? 'Edit Automation' : 'Create a new automation'}
            subtitle="Set the order of lists every ticket of the board must follow"
            maxWidth="960px"
            footer={footer}
        >
            <div className="automation-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-700 w-[60px]">When</span>
                    <AppSelect
                        portal
                        value={LIST_PROCESS_TYPE}
                        style={{ ...selectStyle, color: '#1f2937', minWidth: '260px' }}
                        onChange={(e) => onTypeChange(e.target.value)}
                        options={WHEN_OPTIONS}
                    />
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span style={labelStyle}>BOARD</span>
                    <AppSelect
                        portal
                        value={boardId}
                        onChange={(e) => {
                            setBoardId(e.target.value);
                            setSteps([emptyStep(), emptyStep()]);
                        }}
                        placeholder="Select board"
                        style={selectStyle}
                        options={boardName || []}
                    />
                </div>
                {existingRule && (
                    <div style={{ fontSize: '12.5px', color: '#b91c1c', background: '#fef2f2', borderRadius: '6px', padding: '6px 10px' }}>
                        This board already has a ticket process ({existingRule.name}). Edit or delete that one instead.
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {steps.map((step, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-3 flex-wrap"
                            style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px' }}
                        >
                            <span style={{ ...labelStyle, width: '28px' }}>{i + 1}</span>
                            <span className="text-sm text-gray-700" style={{ minWidth: '90px' }}>
                                {i === 0 ? 'Add to list' : 'Move to list'}
                            </span>
                            <AppSelect
                                portal
                                value={step.listId}
                                onChange={(e) => updateStep(i, { listId: e.target.value })}
                                placeholder={boardId ? 'Select list' : 'Select a board first'}
                                disabled={!boardId}
                                style={selectStyle}
                                options={listOptions.filter(
                                    (o) => o.value === step.listId || !steps.some((s, j) => j !== i && s.listId === o.value),
                                )}
                            />
                            <AppSelect
                                portal
                                value={step.mandatory ? 'mandatory' : 'optional'}
                                onChange={(e) => updateStep(i, { mandatory: e.target.value === 'mandatory' })}
                                style={{ ...selectStyle, minWidth: '140px' }}
                                options={REQUIREMENT_OPTIONS}
                            />
                            <button type="button" title="Move up" style={iconBtn} disabled={i === 0} onClick={() => moveStep(i, -1)}>
                                <FiArrowUp size={13} />
                            </button>
                            <button
                                type="button"
                                title="Move down"
                                style={iconBtn}
                                disabled={i === steps.length - 1}
                                onClick={() => moveStep(i, 1)}
                            >
                                <FiArrowDown size={13} />
                            </button>
                            {steps.length > 2 && (
                                <button
                                    type="button"
                                    title="Remove step"
                                    onClick={() => setSteps((prev) => prev.filter((_, j) => j !== i))}
                                    style={{ ...iconBtn, color: '#ef4444', background: '#fef2f2', borderColor: '#fecaca' }}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                    <div>
                        <button
                            type="button"
                            className="automation-add-btn"
                            disabled={!boardId || steps.length >= listOptions.length}
                            style={{ opacity: boardId ? 1 : 0.5 }}
                            onClick={() => setSteps((prev) => [...prev, emptyStep()])}
                        >
                            <FiPlus size={13} />
                            Add step {steps.length + 1}
                        </button>
                    </div>
                </div>

                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>
                    {entryNames.length > 0 && (
                        <div>
                            New tickets and tickets coming from other boards can only go to: <b>{entryNames.join(', ')}</b>.
                        </div>
                    )}
                    <div>
                        A ticket cannot skip a mandatory step when it moves forward; optional steps can be skipped. Moving back is
                        always allowed. Lists that are not in the process stay free for tickets already on this board.
                    </div>
                    <div>
                        Manual moves that break the order are refused. Tickets moved or created by automations are put in the next
                        required list instead.
                    </div>
                </div>
            </div>
        </AppModal>
    );
};

export default ListProcessForm;
